import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Mail } from 'lucide-react';
import {
  invoiceStatusOptions,
  type DashboardSummary,
  type Invoice,
  type InvoiceStatus,
  type InvoiceView,
} from '@fpc/shared';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { useLayout } from '@/hooks/useMediaQuery';
import { useMoney } from '@/hooks/useMoney';
import { StatusSummary, type StatusSegment } from '@/components/StatusSummary';
import { ResponsiveRecords, type ResponsiveRecord } from '@/components/ResponsiveRecords';
import { EXTRACTION_POLL_MS, isExtracting, useExtractionStall } from '@/lib/extraction';
import { formatDate, humanize, isOverdue } from '@/lib/format';
import { extractInvoiceText } from '@/services/ocr.service';
import {
  EmptyState,
  ErrorState,
  FileField,
  Modal,
  Money,
  PageHeader,
  Pagination,
  SearchInput,
  Spinner,
  StatusBadge,
  Table,
  Tabs,
} from '@/components/ui';

type View = InvoiceView;

const VIEWS: Array<{ key: View; label: string }> = [
  { key: 'ALL', label: 'All' },
  { key: 'REVIEW', label: 'Needs review' },
  { key: 'PENDING_APPROVAL', label: 'Pending approval' },
  { key: 'ACCOUNTING', label: 'With accounting' },
  { key: 'TREASURY', label: 'With Treasury' },
  { key: 'APPROVED', label: 'Approved' },
  { key: 'PAYMENT_PENDING', label: 'Payment pending' },
  { key: 'PAID', label: 'Paid' },
  { key: 'OVERDUE', label: 'Overdue' },
];

/**
 * The status card's stages, keyed by the list view each one filters to. Overdue
 * cuts across the others, so it is listed but kept out of the bar.
 */
function invoiceStages(
  invoices: DashboardSummary['invoices'],
  compact: (minor: number) => string,
): StatusSegment[] {
  return [
    {
      key: 'REVIEW',
      label: 'Needs review',
      colour: '#F59E0B',
      count: invoices.pendingReview,
      weight: invoices.pendingReview,
    },
    {
      key: 'PENDING_APPROVAL',
      label: 'Approval',
      colour: '#105261',
      count: invoices.pendingApproval,
      value: compact(invoices.pendingApprovalAmount),
      weight: invoices.pendingApproval,
    },
    {
      key: 'ACCOUNTING',
      label: 'Accounting',
      colour: '#18778B',
      count: invoices.accountingVerification,
      value: compact(invoices.accountingVerificationAmount),
      weight: invoices.accountingVerification,
    },
    {
      key: 'TREASURY',
      label: 'Treasury',
      colour: '#40B1C9',
      count: invoices.treasuryApproval,
      value: compact(invoices.treasuryApprovalAmount),
      weight: invoices.treasuryApproval,
    },
    {
      key: 'APPROVED',
      label: 'Approved',
      colour: '#65A30D',
      count: invoices.approvedUnpaid,
      value: compact(invoices.approvedUnpaidAmount),
      weight: invoices.approvedUnpaid,
    },
    {
      key: 'OVERDUE',
      label: 'Overdue',
      colour: '#DC2626',
      count: invoices.overdue,
      value: compact(invoices.overdueAmount),
    },
  ];
}

/** Invoice register and review queue — PRD §36 `/invoices`, `/invoices/review`. */
export function InvoicesPage({ initialView = 'ALL' }: { initialView?: View }) {
  const { companyId, verticalId, can } = useAuth();
  const layout = useLayout();
  const money = useMoney();
  const [params, setParams] = useSearchParams();
  // Opened by the shell's "Upload invoice" action, which can only reach this
  // page-local state through the URL.
  const [uploadOpen, setUploadOpen] = useState(params.get('upload') === '1');

  const view = (params.get('view') as View) ?? initialView;
  const page = Number(params.get('page') ?? 1);
  const search = params.get('q') ?? '';
  const status = params.get('status') ?? '';

  // A row that is still being read changes on its own; keep asking until none
  // are (or until the ceiling), so its status does not sit on "Extracting".
  const stalledRef = useRef(false);
  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', companyId, verticalId, view, page, search, status],
    queryFn: () =>
      api.invoices.list({
        companyId,
        verticalId,
        view,
        page,
        pageSize: 25,
        q: search || undefined,
        status: status || undefined,
      }),
    refetchInterval: (query) =>
      query.state.data?.items.some((invoice) => isExtracting(invoice.status)) && !stalledRef.current
        ? EXTRACTION_POLL_MS
        : false,
  });
  const stall = useExtractionStall(
    data?.items.some((invoice) => isExtracting(invoice.status)) ?? false,
  );
  useEffect(() => {
    stalledRef.current = stall.stalled;
  }, [stall.stalled]);

  // The same summary the dashboard loads (one cache entry), for the status card.
  const { data: summary } = useQuery({
    queryKey: ['dashboard', companyId, verticalId],
    queryFn: () => api.dashboard.summary({ companyId, verticalId }),
    enabled: layout !== 'desktop' && can('dashboard:read'),
  });
  const inProgress = summary
    ? summary.invoices.pendingReview +
      summary.invoices.pendingApproval +
      summary.invoices.accountingVerification +
      summary.invoices.treasuryApproval +
      summary.invoices.approvedUnpaid
    : 0;

  const update = (next: Record<string, string>) => {
    const merged = new URLSearchParams(params);
    for (const [key, value] of Object.entries(next)) {
      if (value) merged.set(key, value);
      else merged.delete(key);
    }
    if (!('page' in next)) merged.set('page', '1');
    setParams(merged);
  };

  // A stage narrows the exact statuses on offer; one chosen under the old
  // stage that the new one cannot contain is dropped rather than left to
  // match nothing.
  const changeView = (next: View) =>
    update({
      view: next,
      status: invoiceStatusOptions(next).includes(status as InvoiceStatus) ? status : '',
    });

  return (
    <>
      <PageHeader
        title="Invoices"
        subtitle="Everything received, from any source"
        inlineActions={layout === 'phone'}
        actions={
          !can('invoice:create') ? null : layout === 'phone' ? (
            // On a phone, upload is the shell's "+" button; the mailbox is an icon.
            can('mail_connection:manage') ? (
              <Link
                className="btn-icon"
                to="/integrations/outlook"
                aria-label="Invoice Mailbox"
                title="Invoice Mailbox"
              >
                <Mail className="h-5 w-5" aria-hidden="true" />
              </Link>
            ) : null
          ) : layout === 'tablet' ? (
            // Upload is the shell's "+" button on a tablet too.
            can('mail_connection:manage') ? (
              <Link className="btn-secondary" to="/integrations/outlook">
                Invoice Mailbox
              </Link>
            ) : null
          ) : (
            <>
              {/* Discovery only — the mailbox screen owns the connection. */}
              {can('mail_connection:manage') ? (
                <Link className="btn-secondary mr-2" to="/integrations/outlook">
                  Invoice Mailbox
                </Link>
              ) : null}
              <button className="btn-primary" onClick={() => setUploadOpen(true)}>
                Upload invoice
              </button>
            </>
          )
        }
      />

      {summary && layout !== 'desktop' ? (
        // Phone and tablet: where invoices stand, each stage a filter for the list.
        <StatusSummary
          title="Invoice status"
          headline={inProgress.toLocaleString('en-IN')}
          caption="in progress"
          aside={`${summary.invoices.received.toLocaleString('en-IN')} received`}
          unit={['invoice', 'invoices']}
          active={view}
          onSelect={(key) => changeView(key === view ? 'ALL' : (key as View))}
          segments={invoiceStages(summary.invoices, money.compact)}
        />
      ) : null}

      <div className="card">
        {layout === 'desktop' ? (
          <>
            <Tabs tabs={VIEWS} active={view} onChange={changeView} />

            <div className="flex flex-wrap gap-3 border-b border-slate-200 px-4 py-3">
              <input
                className="input max-w-xs"
                placeholder="Invoice number, vendor or PO number…"
                defaultValue={search}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    update({ q: (event.target as HTMLInputElement).value });
                  }
                }}
              />
              <StatusSelect
                view={view}
                status={status}
                onChange={(next) => update({ status: next })}
              />
            </div>
          </>
        ) : (
          // Phone and tablet: search, then Stage and Status as two named
          // dropdowns; the stage decides which statuses the second offers.
          <div className="flex flex-col gap-3 border-b border-ink-100 p-4 md:flex-row md:items-end">
            <div className="md:flex-1">
              <SearchInput
                label="Search invoices"
                placeholder="Invoice number, vendor or PO number…"
                defaultValue={search}
                onSearch={(value) => update({ q: value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-2 md:flex md:gap-3">
              <label className="block md:w-48">
                <span className="label">Stage</span>
                <select
                  className="input"
                  value={view}
                  onChange={(event) => changeView(event.target.value as View)}
                >
                  {VIEWS.map((entry) => (
                    <option key={entry.key} value={entry.key}>
                      {entry.key === 'ALL' ? 'All stages' : entry.label}
                    </option>
                  ))}
                </select>
              </label>
              <label className="block md:w-52">
                <span className="label">Status</span>
                <StatusSelect
                  view={view}
                  status={status}
                  className="w-full"
                  onChange={(next) => update({ status: next })}
                />
              </label>
            </div>
          </div>
        )}

        {isLoading ? (
          <Spinner />
        ) : error && !data ? (
          // Only a failure to load the list at all; a failed background re-check
          // must not replace rows that are already on screen.
          <div className="p-4">
            <ErrorState error={error} />
          </div>
        ) : !data?.items.length ? (
          <EmptyState
            illustration="review"
            title="No invoices here"
            hint={
              view === 'REVIEW'
                ? 'Invoices appear here once extraction completes.'
                : 'Upload an invoice, or forward one to the company invoice mailbox.'
            }
          />
        ) : (
          <>
            <ResponsiveRecords
              layout={layout}
              openLabel="Open invoice"
              records={data.items.map(invoiceRecord)}
              table={
                <Table>
                  <thead className="thead">
                    <tr>
                      <th className="th">Tracking ID</th>
                      <th className="th">Invoice</th>
                      <th className="th">PO number</th>
                      <th className="th">Vendor</th>
                      <th className="th">Invoice date</th>
                      <th className="th">Due</th>
                      <th className="th text-right">Amount</th>
                      <th className="th">Status</th>
                      <th className="th">Source</th>
                    </tr>
                  </thead>
                  <tbody className="tbody">
                    {data.items.map((invoice) => (
                      <tr key={invoice.id} className="hover:bg-slate-50">
                        <td className="td">
                          <Link
                            className="font-mono text-xs text-brand-700"
                            to={`/invoices/${invoice.id}`}
                          >
                            {invoice.trackingId}
                          </Link>
                        </td>
                        <td className="td">
                          <Link
                            className="font-medium text-brand-700"
                            to={`/invoices/${invoice.id}`}
                          >
                            {invoice.invoiceNumber ?? invoice.documentFileName ?? 'Untitled'}
                          </Link>
                          {needsAttention(invoice) ? (
                            <span className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
                              Needs attention
                            </span>
                          ) : null}
                        </td>
                        {/* The PO linked on the review screen, if any. */}
                        <td className="td font-mono text-xs">
                          {invoice.purchaseOrder?.poNumber ?? <span className="muted">—</span>}
                        </td>
                        <td className="td">{invoice.vendorName ?? '—'}</td>
                        <td className="td">{formatDate(invoice.invoiceDate)}</td>
                        <td className={`td ${isOverdue(invoice.dueDate) ? 'text-red-600' : ''}`}>
                          {formatDate(invoice.dueDate)}
                        </td>
                        <td className="td text-right">
                          <Money minor={invoice.totalAmount} />
                        </td>
                        <td className="td">
                          <StatusBadge status={invoice.status} />
                        </td>
                        <td className="td text-xs text-slate-500">{humanize(invoice.source)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              }
            />
            <Pagination
              page={data.page}
              pageSize={data.pageSize}
              total={data.total}
              onChange={(next) => update({ page: String(next) })}
            />
          </>
        )}
      </div>

      {uploadOpen ? <UploadInvoiceModal onClose={() => setUploadOpen(false)} /> : null}
    </>
  );
}

/**
 * The exact status, from only those the chosen stage can contain. A stage
 * that is a single status has nothing left to choose, so the list is off.
 */
function StatusSelect({
  view,
  status,
  onChange,
  className = 'w-auto',
}: {
  view: View;
  status: string;
  onChange(status: string): void;
  className?: string;
}) {
  const options = invoiceStatusOptions(view);
  return (
    <select
      className={`input ${className}`}
      aria-label="Invoice status"
      value={options.includes(status as InvoiceStatus) ? status : ''}
      disabled={options.length <= 1}
      onChange={(event) => onChange(event.target.value)}
    >
      <option value="">Any status</option>
      {options.map((entry) => (
        <option key={entry} value={entry}>
          {humanize(entry)}
        </option>
      ))}
    </select>
  );
}

/** An open validation problem that someone has to look at. */
function needsAttention(invoice: Invoice): boolean {
  return (
    invoice.findings?.some((finding) => !finding.resolved && finding.severity !== 'INFO') ?? false
  );
}

function AttentionBadge() {
  return (
    <span className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800">
      Needs attention
    </span>
  );
}

/**
 * An invoice for the phone and tablet lists: the tablet card is titled with
 * the vendor and the tracking ID; a phone row shows the tracking ID.
 */
function invoiceRecord(invoice: Invoice): ResponsiveRecord {
  return {
    key: invoice.id,
    href: `/invoices/${invoice.id}`,
    title: invoice.vendorName ?? 'Vendor not read yet',
    subtitle: invoice.trackingId,
    heading: <span className="font-mono">{invoice.trackingId}</span>,
    badge: <StatusBadge status={invoice.status} />,
    details: [
      { label: 'Invoice', value: invoice.invoiceNumber ?? invoice.documentFileName ?? 'Untitled' },
      { label: 'Vendor', value: invoice.vendorName ?? '—', notOnCard: true },
      { label: 'PO number', value: invoice.purchaseOrder?.poNumber ?? '—' },
      { label: 'Amount', value: <Money minor={invoice.totalAmount} /> },
      { label: 'Invoice date', value: formatDate(invoice.invoiceDate) },
      {
        label: 'Due',
        value: formatDate(invoice.dueDate),
        className: isOverdue(invoice.dueDate) ? 'text-red-600' : undefined,
      },
      { label: 'Source', value: humanize(invoice.source) },
    ],
    extra: needsAttention(invoice) ? <AttentionBadge /> : undefined,
  };
}

/** Names what read the document: the PDF's own text, OCR, or both across pages. */
function engineOf(pages: Array<{ source: 'TEXT_LAYER' | 'OCR' }>): string {
  const sources = new Set(pages.map((page) => page.source));
  if (sources.size > 1) return 'pdf.js + tesseract.js';
  return sources.has('TEXT_LAYER') ? 'pdf.js' : 'tesseract.js';
}

export function UploadInvoiceModal({ onClose }: { onClose(): void }) {
  const { companyId } = useAuth();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [phase, setPhase] = useState<'reading' | 'uploading'>('reading');
  // Reading can take several seconds; closing the dialog meanwhile must not
  // still upload the file once the text comes back.
  const closed = useRef(false);
  const close = () => {
    closed.current = true;
    onClose();
  };

  const mutation = useMutation({
    mutationFn: async () => {
      setPhase('reading');
      let result: Awaited<ReturnType<typeof extractInvoiceText>> | undefined;
      try {
        result = await extractInvoiceText(file!);
      } catch (error) {
        // Reading in the browser is a head start, not a gate: without it the
        // server reads the stored file with its own driver, as it always has.
        console.warn('Browser OCR failed; uploading without it:', error);
      }
      // Any currency goes up: the server books the invoice in its company's
      // currency and flags a document billed in another one for review.
      const rawOcr = result ? { engine: engineOf(result.pages), pages: result.pages } : undefined;
      if (closed.current) throw new Error('Upload cancelled');
      setPhase('uploading');
      return api.invoices.upload(file!, companyId!, undefined, rawOcr);
    },
    onSuccess: (invoice) => {
      void queryClient.invalidateQueries({ queryKey: ['invoices'] });
      onClose();
      navigate(`/invoices/${invoice.id}`);
    },
  });

  return (
    <Modal
      title="Upload invoice"
      onClose={close}
      footer={
        <>
          <button className="btn-secondary" onClick={close}>
            Cancel
          </button>
          <button
            className="btn-primary"
            disabled={!file || !companyId || mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            {mutation.isPending ? (phase === 'reading' ? 'Reading text…' : 'Uploading…') : 'Upload'}
          </button>
        </>
      }
    >
      <p className="mb-4 text-sm text-slate-600">
        PDF, JPG or PNG. The text is read in your browser first, then matched to the invoice fields;
        the invoice will appear in the review queue once they have been filled.
      </p>
      <p className="notice-info mb-4">
        The invoice is booked in the selected company&apos;s currency. One billed in a different
        currency is flagged for review before it can be submitted.
      </p>
      <FileField
        id="invoice-file"
        file={file}
        accept="application/pdf,image/jpeg,image/png"
        onChange={(next) => {
          setFile(next);
          // A warning about the previous file does not apply to the next one.
          mutation.reset();
        }}
        disabled={mutation.isPending}
      />
      {mutation.error ? (
        <div className="mt-3">
          <ErrorState error={mutation.error} />
        </div>
      ) : null}
    </Modal>
  );
}
